import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { SnackbarService } from '../../../../../../core/services';
import { ConfirmComponent } from '../../../../../../components/core/confirm/confirm.component';
import * as TaskActions from '../../../../../../store/task/task.actions';
import { selectTaskDetail, selectTaskLoadingDetail, selectTaskSaving, selectTaskUploadingAttachment } from '../../../../../../store/task/task.selectors';
import { selectUser } from '../../../../../../store/user/user.selectors';
import type { TaskAttachment, TaskColumn, TaskItem, TaskPriority, TaskSubtask, TaskTimeEntry, TaskUpsertPayload } from '../../../../../../store/task/task.models';
import type { User } from '../../../../../../store/user/user.models';
import { TaskDialogComponent } from '../tasks-screen.component';
import { renderTaskDescription } from '../task-description.utils';

@Component({
  selector: 'app-task-details-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
  ],
  templateUrl: './task-details-panel.component.html',
  styleUrl: './task-details-panel.component.scss',
})
export class TaskDetailsPanelComponent implements OnChanges {
  @Input({ required: true }) taskId!: string;
  @Input() task: TaskItem | null = null;
  @Input() organizationUsers: User[] = [];
  @Input() statuses: ReadonlyArray<TaskColumn> = [];
  @Input() priorities: ReadonlyArray<{ value: TaskPriority; label: string }> = [];
  @Input() canEdit = false;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly taskUpdated = new EventEmitter<TaskItem>();

  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly snackbar = inject(SnackbarService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  readonly taskSignal = signal<TaskItem | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly uploadingAttachment = signal(false);
  readonly currentUser = signal<User | null>(null);
  readonly editingSubtaskId = signal<string | null>(null);
  readonly editingTimeEntryId = signal<string | null>(null);
  readonly timerSubtaskId = signal('');
  readonly commentForm = this.fb.nonNullable.group({
    body: ['', [Validators.required, Validators.minLength(2)]],
  });
  readonly timeEntryForm = this.fb.nonNullable.group({
    subtask_id: [''],
    minutes: [15, [Validators.required, Validators.min(1)]],
    note: [''],
  });
  readonly subtaskForm = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.minLength(2)]],
    assignee_user_id: [''],
  });
  readonly subtaskEditForm = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.minLength(2)]],
    assignee_user_id: [''],
  });
  readonly timeEntryEditForm = this.fb.nonNullable.group({
    subtask_id: [''],
    minutes: [15, [Validators.required, Validators.min(1)]],
    note: [''],
  });
  readonly comments = computed(() => [...(this.taskSignal()?.comments ?? [])].sort((left, right) => right.created_at.localeCompare(left.created_at)));
  readonly subtasks = computed(() => this.taskSignal()?.subtasks ?? []);
  readonly attachments = computed(() => [...(this.taskSignal()?.attachments ?? [])].sort((left, right) => right.created_at.localeCompare(left.created_at)));
  readonly timeEntries = computed(() => [...(this.taskSignal()?.time_entries ?? [])].sort((left, right) => right.created_at.localeCompare(left.created_at)));
  readonly activity = computed(() => [...(this.taskSignal()?.activity ?? [])].sort((left, right) => right.created_at.localeCompare(left.created_at)));
  readonly completedSubtasks = computed(() => this.subtasks().filter((subtask) => subtask.is_completed).length);
  readonly statusLabel = computed(() => this.statuses.find((status) => status.key === this.taskSignal()?.status)?.label ?? 'Task');
  readonly activeTimer = computed(() => this.timeEntries().find((entry) => !entry.ended_at) ?? null);

  constructor() {
    this.store.select(selectTaskDetail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((task) => {
        if (task && task.id === this.taskId) {
          this.taskSignal.set(task);
          this.taskUpdated.emit(task);
        }
      });

    this.store.select(selectTaskLoadingDetail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.loading.set(loading));

    this.store.select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => this.currentUser.set(user));

    this.store.select(selectTaskSaving)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((saving) => this.saving.set(saving));

    this.store.select(selectTaskUploadingAttachment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((uploading) => this.uploadingAttachment.set(uploading));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('task' in changes) {
      this.taskSignal.set(this.task ?? null);
    }
    if ('taskId' in changes && this.taskId) {
      this.store.dispatch(TaskActions.TaskDetailFetchRequest({ taskId: this.taskId }));
    }
  }

  openEditDialog(): void {
    const task = this.taskSignal();
    if (!task) {
      return;
    }

    const ref = this.dialog.open(TaskDialogComponent, {
      width: '560px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        title: 'Edit Task',
        task,
        projectId: task.project_id,
        organizationUsers: this.organizationUsers,
        statuses: this.statuses,
        priorities: this.priorities,
      },
    });

    ref.afterClosed().subscribe((result?: Record<string, unknown>) => {
      if (!result) {
        return;
      }
      this.submitTaskUpdate(task, result as TaskUpsertPayload, 'Task updated.');
    });
  }

  updateQuickField(field: 'status' | 'priority' | 'start_at' | 'due_at' | 'estimated_minutes' | 'tracked_minutes', value: string): void {
    const task = this.taskSignal();
    if (!task) {
      return;
    }
    this.submitTaskUpdate(task, this.buildTaskPayload(task, {
      status: field === 'status' ? value : task.status,
      priority: field === 'priority' ? value : task.priority,
      start_at: field === 'start_at' ? value : (task.start_at ?? ''),
      due_at: field === 'due_at' ? value : (task.due_at ?? ''),
      estimated_minutes: field === 'estimated_minutes' ? this.toNonNegativeNumber(value) : (task.estimated_minutes ?? 0),
      tracked_minutes: field === 'tracked_minutes' ? this.toNonNegativeNumber(value) : (task.tracked_minutes ?? 0),
    }), undefined, true);
  }

  addComment(): void {
    if (this.commentForm.invalid || !this.taskSignal()) {
      this.commentForm.markAllAsTouched();
      return;
    }
    this.store.dispatch(TaskActions.TaskCommentCreateRequest({
      taskId: this.taskId,
      body: this.commentForm.getRawValue().body.trim(),
    }));
    this.commentForm.reset({ body: '' });
  }

  addTimeEntry(): void {
    if (this.timeEntryForm.invalid || !this.taskSignal()) {
      this.timeEntryForm.markAllAsTouched();
      return;
    }

    const raw = this.timeEntryForm.getRawValue();
    const subtaskId = raw.subtask_id.trim();
    if (!this.canLogTimeForSubtask(subtaskId || null)) {
      this.snackbar.showNotification('error', '', 'You can only log time on subtasks assigned to you.', 'Close');
      return;
    }
    this.store.dispatch(TaskActions.TaskTimeEntryCreateRequest({
      taskId: this.taskId,
      payload: {
        subtask_id: subtaskId || undefined,
        minutes: Number(raw.minutes) || 0,
        note: raw.note.trim(),
      },
    }));
    this.timeEntryForm.reset({ subtask_id: '', minutes: 15, note: '' });
  }

  startTimer(): void {
    if (!this.taskSignal() || this.activeTimer()) {
      return;
    }
    if (!this.canLogTimeForSubtask(this.timerSubtaskId().trim() || null)) {
      this.snackbar.showNotification('error', '', 'You can only start a timer on a subtask assigned to you.', 'Close');
      return;
    }
    this.store.dispatch(TaskActions.TaskTimerStartRequest({
      taskId: this.taskId,
      payload: {
        subtask_id: this.timerSubtaskId().trim() || undefined,
      },
    }));
  }

  stopTimer(): void {
    if (!this.taskSignal() || !this.activeTimer()) {
      return;
    }
    this.store.dispatch(TaskActions.TaskTimerStopRequest({ taskId: this.taskId, payload: {} }));
  }

  startTimeEntryEdit(entry: TaskTimeEntry): void {
    this.editingTimeEntryId.set(entry.id);
    this.timeEntryEditForm.reset({
      subtask_id: entry.subtask_id ?? '',
      minutes: entry.minutes || 1,
      note: entry.note ?? '',
    });
  }

  cancelTimeEntryEdit(): void {
    this.editingTimeEntryId.set(null);
    this.timeEntryEditForm.reset({ subtask_id: '', minutes: 15, note: '' });
  }

  saveTimeEntry(entry: TaskTimeEntry): void {
    if (this.timeEntryEditForm.invalid) {
      this.timeEntryEditForm.markAllAsTouched();
      return;
    }

    const raw = this.timeEntryEditForm.getRawValue();
    const subtaskId = raw.subtask_id.trim();
    if (!this.canManageTimeEntry(entry) || !this.canLogTimeForSubtask(subtaskId || null)) {
      this.snackbar.showNotification('error', '', 'You cannot update time for this subtask.', 'Close');
      return;
    }
    this.store.dispatch(TaskActions.TaskTimeEntryUpdateRequest({
      taskId: this.taskId,
      timeEntryId: entry.id,
      payload: {
        subtask_id: subtaskId || undefined,
        minutes: Number(raw.minutes) || 0,
        note: raw.note.trim(),
        started_at: entry.started_at ?? undefined,
        ended_at: entry.ended_at ?? undefined,
      },
    }));
    this.cancelTimeEntryEdit();
  }

  deleteTimeEntry(entry: TaskTimeEntry): void {
    if (!this.canManageTimeEntry(entry)) {
      this.snackbar.showNotification('error', '', 'You cannot delete this time entry.', 'Close');
      return;
    }
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Delete Time Entry',
        message: `Delete the ${this.formatMinutes(entry.minutes)} time entry? This action cannot be undone.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.store.dispatch(TaskActions.TaskTimeEntryDeleteRequest({ taskId: this.taskId, timeEntryId: entry.id }));
      if (this.editingTimeEntryId() === entry.id) {
        this.cancelTimeEntryEdit();
      }
    });
  }

  addSubtask(): void {
    if (this.subtaskForm.invalid || !this.taskSignal()) {
      this.subtaskForm.markAllAsTouched();
      return;
    }
    this.store.dispatch(TaskActions.TaskSubtaskCreateRequest({
      taskId: this.taskId,
      title: this.subtaskForm.getRawValue().title.trim(),
      assignee_user_id: this.subtaskForm.getRawValue().assignee_user_id.trim() || undefined,
    }));
    this.subtaskForm.reset({ title: '', assignee_user_id: '' });
  }

  toggleSubtask(subtask: TaskSubtask, checked: boolean): void {
    if (!this.canToggleSubtask(subtask)) {
      this.snackbar.showNotification('error', '', 'Only the assigned subtask owner can close or reopen it.', 'Close');
      return;
    }
    this.store.dispatch(TaskActions.TaskSubtaskUpdateRequest({
      taskId: this.taskId,
      subtaskId: subtask.id,
      title: subtask.title,
      is_completed: checked,
      assignee_user_id: subtask.assignee_user_id ?? '',
    }));
  }

  startSubtaskEdit(subtask: TaskSubtask): void {
    this.editingSubtaskId.set(subtask.id);
    this.subtaskEditForm.reset({ title: subtask.title, assignee_user_id: subtask.assignee_user_id ?? '' });
  }

  cancelSubtaskEdit(): void {
    this.editingSubtaskId.set(null);
    this.subtaskEditForm.reset({ title: '', assignee_user_id: '' });
  }

  saveSubtaskTitle(subtask: TaskSubtask): void {
    if (this.subtaskEditForm.invalid) {
      this.subtaskEditForm.markAllAsTouched();
      return;
    }
    this.store.dispatch(TaskActions.TaskSubtaskUpdateRequest({
      taskId: this.taskId,
      subtaskId: subtask.id,
      title: this.subtaskEditForm.getRawValue().title.trim(),
      is_completed: subtask.is_completed,
      assignee_user_id: this.subtaskEditForm.getRawValue().assignee_user_id.trim(),
    }));
    this.cancelSubtaskEdit();
  }

  deleteSubtask(subtask: TaskSubtask): void {
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Delete Subtask',
        message: `Delete "${subtask.title}"? This action cannot be undone.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(TaskActions.TaskSubtaskDeleteRequest({ taskId: this.taskId, subtaskId: subtask.id }));
      if (this.editingSubtaskId() === subtask.id) {
        this.cancelSubtaskEdit();
      }
    });
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file || !this.taskSignal()) {
      if (input) {
        input.value = '';
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.snackbar.showNotification('error', '', 'Attachment must be 10MB or smaller', 'Close');
      input.value = '';
      return;
    }

    this.store.dispatch(TaskActions.TaskAttachmentUploadRequest({ taskId: this.taskId, file }));
    input.value = '';
  }

  openAttachment(attachment: TaskAttachment): void {
    if (!attachment.url || typeof window === 'undefined') {
      return;
    }
    window.open(attachment.url, '_blank', 'noopener,noreferrer');
  }

  downloadAttachment(attachment: TaskAttachment): void {
    const url = attachment.download_url || attachment.url;
    if (!url || typeof document === 'undefined') {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.download = attachment.name || 'attachment';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  isImageAttachment(attachment: TaskAttachment): boolean {
    return Boolean(attachment.type?.startsWith('image/'));
  }

  formatAttachmentSize(size: number | undefined): string {
    if (!size || size <= 0) {
      return '';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  getUserName(userId: string | null | undefined): string {
    if (!userId) {
      return 'Unassigned';
    }
    return this.organizationUsers.find((user) => user.id === userId)?.name?.trim() || 'Unknown user';
  }

  formatDueDate(value: string | null | undefined): string {
    if (!value) {
      return 'No due date';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'No due date';
    }
    return parsed.toLocaleString();
  }

  formatMinutes(value: number | null | undefined): string {
    const minutes = Number(value ?? 0);
    if (!minutes || minutes < 0) {
      return 'None';
    }
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  }

  formatTags(tags: string[] | undefined): string {
    return (tags ?? []).join(', ');
  }

  getTaskAssigneeIds(task: TaskItem | null | undefined): string[] {
    if (!task) {
      return [];
    }
    const ids = (task.subtasks ?? [])
      .map((subtask) => subtask.assignee_user_id ?? '')
      .filter((id) => !!id);
    return [...new Set(ids)];
  }

  getTaskAssigneeNames(task: TaskItem | null | undefined): string {
    const names = this.getTaskAssigneeIds(task)
      .map((userId) => this.getUserName(userId))
      .filter((name) => name !== 'Unassigned');
    if (!names.length) {
      return 'Unassigned';
    }
    return names.join(', ');
  }

  getSubtaskName(subtaskId: string | null | undefined): string {
    if (!subtaskId) {
      return 'No subtask';
    }
    return this.subtasks().find((subtask) => subtask.id === subtaskId)?.title?.trim() || 'Unknown subtask';
  }

  getSubtaskTrackedMinutes(subtaskId: string | null | undefined): number {
    if (!subtaskId) {
      return 0;
    }
    return this.timeEntries().reduce((sum, entry) => {
      if (entry.subtask_id !== subtaskId || !entry.ended_at) {
        return sum;
      }
      return sum + (entry.minutes || 0);
    }, 0);
  }

  formatTimeEntryRange(entry: TaskTimeEntry): string {
    if (!entry.started_at && !entry.ended_at) {
      return this.formatDueDate(entry.created_at);
    }
    if (entry.started_at && !entry.ended_at) {
      return `Running since ${this.formatDueDate(entry.started_at)}`;
    }
    if (entry.started_at && entry.ended_at) {
      return `${this.formatDueDate(entry.started_at)} - ${this.formatDueDate(entry.ended_at)}`;
    }
    return this.formatDueDate(entry.ended_at);
  }

  formatTimeEntrySubtask(entry: TaskTimeEntry): string {
    return this.getSubtaskName(entry.subtask_id);
  }

  renderDescription(value: string | null | undefined): string {
    return renderTaskDescription(value);
  }

  toInputDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  toIsoDate(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const parsed = new Date(`${trimmed}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toISOString();
  }

  private toNonNegativeNumber(value: string): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }
    return Math.floor(parsed);
  }

  formatSubtaskMeta(subtask: TaskSubtask): string {
    const tracked = this.getSubtaskTrackedMinutes(subtask.id);
    const assignee = this.getUserName(subtask.assignee_user_id);
    if (subtask.is_completed && subtask.completed_at) {
      return `${assignee} • ${this.formatMinutes(tracked)} logged • Completed ${this.formatDueDate(subtask.completed_at)}`;
    }
    return `${assignee} • ${this.formatMinutes(tracked)} logged • Created ${this.formatDueDate(subtask.created_at)}`;
  }

  getSubtaskTimeByUser(subtask: TaskSubtask): Array<{ userId: string; name: string; minutes: number }> {
    const buckets = new Map<string, { userId: string; name: string; minutes: number }>();

    for (const entry of this.timeEntries()) {
      if (!entry.ended_at || (entry.subtask_id ?? '') !== subtask.id) {
        continue;
      }
      const userId = (entry.created_by ?? '').trim();
      const key = userId || '';
      const row = buckets.get(key) ?? {
        userId: key,
        name: this.getUserName(userId || null),
        minutes: 0,
      };
      row.minutes += entry.minutes ?? 0;
      buckets.set(key, row);
    }

    return [...buckets.values()].sort((left, right) => right.minutes - left.minutes || left.name.localeCompare(right.name));
  }

  private buildTaskPayload(task: TaskItem, overrides: Partial<TaskUpsertPayload> = {}): TaskUpsertPayload {
    return {
      project_id: task.project_id,
      sprint_id: task.sprint_id ?? '',
      title: task.title,
      description: task.description ?? '',
      status: task.status,
      priority: task.priority,
      start_at: task.start_at ?? '',
      due_at: task.due_at ?? '',
      estimated_minutes: task.estimated_minutes ?? 0,
      tracked_minutes: task.tracked_minutes ?? 0,
      tags: task.tags ?? [],
      ...overrides,
    };
  }

  canManageTimeTracking(): boolean {
    if (this.isTimeTrackingAdmin()) {
      return true;
    }
    return this.availableTimeLoggingSubtasks().length > 0;
  }

  availableTimeLoggingSubtasks(): TaskSubtask[] {
    if (this.isTimeTrackingAdmin()) {
      return this.subtasks();
    }
    const currentUserId = this.currentUser()?.id ?? '';
    return this.subtasks().filter((subtask) => (subtask.assignee_user_id ?? '') === currentUserId);
  }

  canLogTimeForSubtask(subtaskId: string | null | undefined): boolean {
    if (this.isTimeTrackingAdmin()) {
      return true;
    }
    const normalizedSubtaskId = (subtaskId ?? '').trim();
    if (!normalizedSubtaskId) {
      return false;
    }
    const currentUserId = this.currentUser()?.id ?? '';
    return this.subtasks().some((subtask) => subtask.id === normalizedSubtaskId && (subtask.assignee_user_id ?? '') === currentUserId);
  }

  canToggleSubtask(subtask: TaskSubtask | null | undefined): boolean {
    if (!subtask) {
      return false;
    }
    if (this.isTimeTrackingAdmin()) {
      return true;
    }
    const currentUserId = this.currentUser()?.id ?? '';
    return !!currentUserId && (subtask.assignee_user_id ?? '') === currentUserId;
  }

  canManageTimeEntry(entry: TaskTimeEntry): boolean {
    if (this.isTimeTrackingAdmin()) {
      return true;
    }
    const currentUserId = this.currentUser()?.id ?? '';
    if (!currentUserId || entry.created_by !== currentUserId) {
      return false;
    }
    return this.canLogTimeForSubtask(entry.subtask_id ?? null);
  }

  private isTimeTrackingAdmin(): boolean {
    const role = this.currentUser()?.role?.trim().toLowerCase() ?? '';
    return role === 'owner' || role === 'admin';
  }

  private submitTaskUpdate(task: TaskItem, payload: TaskUpsertPayload, successMessage?: string, silentSuccess: boolean = false): void {
    const nextStatus = (payload.status ?? task.status ?? '').trim().toLowerCase();
    if (nextStatus === 'done' && this.hasIncompleteSubtasks(task)) {
      this.dialog.open(ConfirmComponent, {
        width: '420px',
        maxWidth: 'calc(100vw - 24px)',
        data: {
          title: 'Close Task With Open Subtasks?',
          message: `${this.getIncompleteSubtaskCount(task)} subtask${this.getIncompleteSubtaskCount(task) === 1 ? ' is' : 's are'} still incomplete. Mark this task as done anyway?`,
        },
      }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
        if (!confirmed) {
          this.store.dispatch(TaskActions.TaskDetailFetchRequest({ taskId: task.id }));
          return;
        }

        this.dispatchTaskUpdate(task.id, payload, successMessage, silentSuccess);
      });
      return;
    }

    this.dispatchTaskUpdate(task.id, payload, successMessage, silentSuccess);
  }

  private dispatchTaskUpdate(taskId: string, payload: TaskUpsertPayload, successMessage?: string, silentSuccess: boolean = false): void {
    this.store.dispatch(TaskActions.TaskUpdateRequest({
      taskId,
      payload,
      successMessage,
      silentSuccess,
    }));
  }

  private getIncompleteSubtaskCount(task: TaskItem | null | undefined): number {
    return (task?.subtasks ?? []).filter((subtask) => !subtask.is_completed).length;
  }

  private hasIncompleteSubtasks(task: TaskItem | null | undefined): boolean {
    return this.getIncompleteSubtaskCount(task) > 0;
  }
}
